import { type CaseRepository, CaseSortCriteria, CaseListFilter, CaseSearchContext } from '@telespot/domain';

export interface ListWorkspaceCasesCursor {
  id: string;
  name: string;
  createdAt: Date;
}

export interface ListWorkspaceCasesInput {
  workspaceId: string;
  limit: number;
  searchTerm?: string;
  lastItem?: ListWorkspaceCasesCursor;
  sorts?: CaseSortCriteria;
  filters?: CaseListFilter[];
  searchContext?: CaseSearchContext;
}

export interface ListedWorkspaceCase {
  id: string;
  name: string;
  createdAt: Date;
  updatedAt: Date;
  createdById: string;
}

/**
 * Thrown when listing workspace cases fails (e.g. repository error).
 */
export class ListWorkspaceCasesQueryError extends Error {
  constructor(
    message = 'Failed to list workspace cases',
    public readonly cause?: unknown
  ) {
    super(message);
    this.name = 'ListWorkspaceCasesQueryError';
    Object.setPrototypeOf(this, ListWorkspaceCasesQueryError.prototype);
  }
}

/**
 * Use case to list cases in a workspace with optional search and cursor pagination.
 */
export interface IListWorkspaceCasesQuery {
  /**
   * List cases matching the given criteria.
   * @param input Workspace id, limit, optional search term and pagination cursor.
   * @returns The list items.
   * @throws ListWorkspaceCasesQueryError when the operation fails.
   */
  execute(input: ListWorkspaceCasesInput): Promise<ListedWorkspaceCase[]>;
}

export class ListWorkspaceCasesQuery implements IListWorkspaceCasesQuery {
  constructor(private readonly caseRepository: CaseRepository) {}

  public async execute(input: ListWorkspaceCasesInput): Promise<ListedWorkspaceCase[]> {
    try {
      const cases = await this.caseRepository.list(
        input.limit,
        input.lastItem,
        input.searchTerm,
        input.workspaceId,
        input.sorts,
        input.filters,
        input.searchContext
      );
      return cases.map((caseEntity) => ({
        id: caseEntity.id,
        name: caseEntity.name,
        createdAt: caseEntity.createdAt ?? new Date(),
        updatedAt: caseEntity.updatedAt ?? new Date(),
        createdById: caseEntity.createdById,
      }));
    } catch (error) {
      throw new ListWorkspaceCasesQueryError('Failed to list workspace cases', error);
    }
  }
}
